import { WeaponClass } from '../types/character';

export type CombatActionCategory = 'personal' | 'spaceship' | 'aerial';
export type WeaponCraftsmanship = 'poor' | 'common' | 'good' | 'best';
export type AttackType = 'single' | 'semiAuto' | 'fullAuto';

export interface JamRule {
  appliesTo: 'melee' | 'ranged' | 'all';
  minimum: number;
  maximum: number;
  note: string;
}

export interface CraftsmanshipJamModifier {
  craftsmanship: WeaponCraftsmanship;
  effect: 'unreliable' | 'reliable' | 'immune' | 'none';
  description: string;
}

export interface MovementOption {
  allowedWeaponClasses: WeaponClass[];
  distance: 'agilityBonus';
  bsPenalty?: number;
  losesBonus?: boolean;
}

export interface MultipleHitsRule {
  formula: string;
  cap: 'semiAutoRoF' | 'fullAutoRoF';
  allocationRange: number;
  usesMultipleHitsTable: boolean;
}

/**
 * Result of getActionModifiers function containing all combat action modifiers
 * for a given action, weapon class, and movement state.
 *
 * @see Requirements 1.1, 2.1, 3.1, 4.2
 */
export interface ActionModifierResult {
  /** BS bonus from the action (0 if not applicable or conditions not met) */
  bsBonus: number;
  /** BS penalty from the action (negative value, 0 if not applicable) */
  bsPenalty: number;
  /** Whether the weapon class can move during this action */
  canMove: boolean;
  /** BS penalty applied when moving (negative value, 0 if not applicable) */
  movementPenalty: number;
  /** Whether moving causes the character to lose the bsBonus */
  losesBonus: boolean;
  /** Attack type for this action */
  attackType: AttackType;
}

export interface CombatActionMechanic {
  /** Matches the combat action id inside combat-actions.json */
  actionId: string;
  category: CombatActionCategory;
  allowedWeaponClasses: WeaponClass[];
  notes?: string[];
  jamRule?: JamRule;
  bsBonus?: number;
  bsBonusCondition?: string;
  bsPenalty?: number;
  movement?: MovementOption;
  multipleHits?: MultipleHitsRule;
  specialRules?: string[];
  /** Attack type for this action */
  attackType?: AttackType;
}

// Craftsmanship affects jam behavior
export const craftsmanshipJamModifiers: CraftsmanshipJamModifier[] = [
  {
    craftsmanship: 'poor',
    effect: 'unreliable',
    description: 'Unreliable: Jams on 91+. If already Unreliable, jams on any failed hit.'
  },
  {
    craftsmanship: 'common',
    effect: 'none',
    description: 'Standard jam rules apply.'
  },
  {
    craftsmanship: 'good',
    effect: 'reliable',
    description: 'Reliable: On jam, roll d10. Only jams on a 10.'
  },
  {
    craftsmanship: 'best',
    effect: 'immune',
    description: 'Never jams or overheats. Count as miss instead.'
  }
];

// Multiple Hits Table (Table 9-5) for extra hit locations
export const multipleHitsTable: Record<string, string[]> = {
  Head: ['Head', 'Arm', 'Body', 'Arm', 'Body'],
  Arm: ['Arm', 'Body', 'Head', 'Body', 'Arm'],
  Body: ['Body', 'Arm', 'Head', 'Arm', 'Body'],
  Leg: ['Leg', 'Body', 'Arm', 'Head', 'Body']
};

export const combatActionMechanics: Record<string, CombatActionMechanic> = {
  'standard-attack': {
    actionId: 'standard-attack',
    category: 'personal',
    allowedWeaponClasses: [
      WeaponClass.Melee,
      WeaponClass.Pistol,
      WeaponClass.Basic,
      WeaponClass.Heavy,
      WeaponClass.Thrown
    ],
    attackType: 'single',
    notes: [
      'Half Action - Single melee or ranged attack',
      'Can benefit from Aim or other situational modifiers',
      'First hit location: reverse the dice result'
    ],
    jamRule: {
      appliesTo: 'ranged',
      minimum: 96,
      maximum: 100,
      note: 'Unmodified 96-100 = Jam (also automatic miss)'
    }
  },

  'semi-auto-burst': {
    actionId: 'semi-auto-burst',
    category: 'personal',
    allowedWeaponClasses: [WeaponClass.Pistol, WeaponClass.Basic, WeaponClass.Heavy],
    attackType: 'semiAuto',
    bsBonus: 10,
    bsBonusCondition: 'stationary',
    notes: [
      'Full Action - Semi-automatic burst',
      '+10 BS if stationary (no movement)',
      'Pistol/Basic may move Ag Bonus metres but lose +10 BS bonus',
      'Two pistols capable of semi-auto can both fire (Two-Weapon Fighting)',
      'Scatter at Point Blank: Scatter and Semi-Auto hits calculated separately'
    ],
    jamRule: {
      appliesTo: 'ranged',
      minimum: 94,
      maximum: 100,
      note: '94-100 = Jam (lower threshold than standard attack)'
    },
    movement: {
      allowedWeaponClasses: [WeaponClass.Pistol, WeaponClass.Basic],
      distance: 'agilityBonus',
      losesBonus: true
    },
    multipleHits: {
      formula: '1 + floor(DoS / 2)',
      cap: 'semiAutoRoF',
      allocationRange: 2,
      usesMultipleHitsTable: true
    }
  },

  'full-auto-burst': {
    actionId: 'full-auto-burst',
    category: 'personal',
    allowedWeaponClasses: [WeaponClass.Pistol, WeaponClass.Basic, WeaponClass.Heavy],
    attackType: 'fullAuto',
    bsBonus: 20,
    bsBonusCondition: 'stationary',
    notes: [
      'Full Action - Fully automatic burst',
      '+20 BS if stationary (no movement)',
      'Pistol/Basic may move Ag Bonus metres with -10 BS penalty',
      'Two pistols capable of full-auto can both fire (Two-Weapon Fighting)'
    ],
    jamRule: {
      appliesTo: 'ranged',
      minimum: 94,
      maximum: 100,
      note: '94-100 = Jam (lower threshold than standard attack)'
    },
    movement: {
      allowedWeaponClasses: [WeaponClass.Pistol, WeaponClass.Basic],
      distance: 'agilityBonus',
      bsPenalty: -10
    },
    multipleHits: {
      formula: '1 + DoS',
      cap: 'fullAutoRoF',
      allocationRange: 2,
      usesMultipleHitsTable: true
    }
  },

  'suppressing-fire': {
    actionId: 'suppressing-fire',
    category: 'personal',
    allowedWeaponClasses: [WeaponClass.Pistol, WeaponClass.Basic, WeaponClass.Heavy],
    attackType: 'fullAuto',
    bsPenalty: -20,
    notes: [
      'Full Action - Establish 45° kill zone',
      'Requires fully automatic weapon',
      'All in kill zone: Hard (-20) Pinning Test or Pinned',
      '-20 BS to hit random targets (friend or foe)',
      'GM assigns hits randomly within kill zone',
      'Does NOT get +20 Full Auto bonus',
      'Cover/armour still protect',
      'Cannot choose to fail BS test'
    ],
    jamRule: {
      appliesTo: 'ranged',
      minimum: 94,
      maximum: 100,
      note: '94-100 = Jam'
    },
    multipleHits: {
      formula: '1 + floor(DoS / 2)',
      cap: 'fullAutoRoF',
      allocationRange: -1,
      usesMultipleHitsTable: true
    },
    specialRules: ['pinning', 'killZone', 'randomTargets', 'noAutoBonus']
  }
};

export const getCombatActionMechanic = (actionId: string): CombatActionMechanic | undefined =>
  combatActionMechanics[actionId];

/**
 * Get all modifiers for a combat action based on current state.
 *
 * This function centralizes the logic for determining BS bonuses, penalties,
 * movement allowances, and attack types from the combatActionMechanics data.
 *
 * @param actionId - The combat action ID (e.g., 'semi-auto-burst')
 * @param weaponClass - The weapon class being used
 * @param isMoving - Whether the character is moving during the action
 * @returns ActionModifierResult with all applicable modifiers
 *
 * @see Requirements 1.1, 1.2, 1.4, 2.1, 2.2, 3.1, 3.2, 3.4, 4.2, 4.3
 */
export function getActionModifiers(
  actionId: string,
  weaponClass: WeaponClass,
  isMoving: boolean
): ActionModifierResult {
  const mechanic = getCombatActionMechanic(actionId);

  // Handle unknown action IDs with safe defaults
  if (!mechanic) {
    return {
      bsBonus: 0,
      bsPenalty: 0,
      canMove: false,
      movementPenalty: 0,
      losesBonus: false,
      attackType: 'single'
    };
  }

  // Determine if weapon class can move during this action (Req 3.1, 3.2)
  const canMove = mechanic.movement?.allowedWeaponClasses.includes(weaponClass) ?? false;

  // Get losesBonus flag from movement options (Req 3.4)
  const losesBonus = mechanic.movement?.losesBonus ?? false;

  // Get movement penalty (Req 2.2)
  const movementPenalty = mechanic.movement?.bsPenalty ?? 0;

  // Calculate effective BS bonus (Req 1.1, 1.2, 1.4)
  let effectiveBsBonus = 0;
  if (mechanic.bsBonus) {
    // Check if stationary condition is met (Req 1.4)
    const stationaryConditionMet = mechanic.bsBonusCondition === 'stationary' ? !isMoving : true;

    // Check if bonus is lost due to movement (Req 3.4)
    const notLostToMovement = !(isMoving && losesBonus);

    if (stationaryConditionMet && notLostToMovement) {
      effectiveBsBonus = mechanic.bsBonus;
    }
  }

  // Calculate effective BS penalty (Req 2.1, 2.2)
  let effectiveBsPenalty = mechanic.bsPenalty ?? 0;
  if (isMoving && movementPenalty !== 0) {
    effectiveBsPenalty += movementPenalty;
  }

  return {
    bsBonus: effectiveBsBonus,
    bsPenalty: effectiveBsPenalty,
    canMove,
    movementPenalty,
    losesBonus,
    attackType: mechanic.attackType ?? 'single' // Default to 'single' (Req 4.3)
  };
}
